import { notificationCacheMiddleware } from "../lib/notificationCache";
import { configureStore } from "@reduxjs/toolkit";
import { combineReducers } from "@reduxjs/toolkit";
import { createReportCacheWriter } from "../lib/reportCache.js";

// Import reducers
import siteOverviewDataReducer from "./slices/siteOverviewDataSlice";
import observationsReducer from "./slices/observationSlice";
import worksReducer from "./slices/workSlice";
import authReducer from "./slices/authSlice";
import siteDataReducer from "./slices/siteDataSlice";
import uiReducer from "./slices/uiSlice";
import fileReducer from "./slices/fileSlice";
import notificationReducer from "./slices/notificationSlice";
import auditReportReducer from "./slices/auditReportSlice";
import adminReducer from "./slices/adminSlice";
import dashboardReducer from "./slices/dashboardSlice";
import offlineReducer from "./slices/offlineSlice";
import keyIssueMatrixReducer from "./slices/keyIssueMatrixSlice";
// Combine all reducers
const rootReducer = combineReducers({
  auth: authReducer,
  observations: observationsReducer,
  siteOverviewData: siteOverviewDataReducer,
  works: worksReducer,
  siteData: siteDataReducer,

  dashboard: dashboardReducer,
  ui: uiReducer,
  admin: adminReducer,
  files: fileReducer,
  notifications: notificationReducer,
  auditReports: auditReportReducer,
  offline: offlineReducer,
  keyIssueMatrix: keyIssueMatrixReducer,
});

// Configure store with Redux Toolkit
const persistReportCache = createReportCacheWriter();
const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        // Ignore these action types
        ignoredActions: ["persist/PERSIST", "persist/REHYDRATE"],
        // Ignore these field paths in all actions
        ignoredActionsPaths: ["meta.arg", "payload.timestamp"],
        // Ignore these paths in the state
        ignoredPaths: ["items.dates"],
      },
    }).concat(notificationCacheMiddleware, (storeApi) => (next) => (action) => {
      const result = next(action);
      if (action.type?.endsWith("/fulfilled") || ["observations/addSyncedObservation", "keyIssueMatrix/applySyncedKeyIssue",
        "auditReports/addSyncedReport", "auditReports/replaceSyncedReportFile", "works/addSyncedWork"].includes(action.type)) {
        const reports = storeApi.getState()?.auditReports?.allReportsData;
        if (reports && typeof window !== "undefined") {
          const reportRecords = Object.values(reports).filter((record) => record?.report?._id);
          // Do not let an early fulfilled action from a fresh PWA session erase
          // the last complete IndexedDB snapshot before the dashboard hydrates.
          if (reportRecords.length > 0) {
            persistReportCache(reportRecords, storeApi.getState().auditReports.recentReports, storeApi.getState().offline.resolvedIds).catch(() => {});
          }
        }
      }
      return result;
    }),
  devTools: process.env.NODE_ENV !== "production",
});

// export type RootState = ReturnType <typeof store.getState>
// export type AppDispatch = typeof store.dispatch

export default store;
